// frontend/src/pages/Login.tsx
import React, { useState } from 'react';
import { Form, Input, Button, Tabs, message } from 'antd';
import { UserOutlined, LockOutlined, PhoneOutlined, MailOutlined, CheckCircleOutlined, SafetyOutlined, FileTextOutlined, TeamOutlined } from '@ant-design/icons';
import { useNavigate, Link } from 'react-router-dom';
import api from '../services/api';
import styles from './Login.module.css';

const { TabPane } = Tabs;

const LoginPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [codeLoading, setCodeLoading] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [loginType, setLoginType] = useState<'password' | 'code'>('password');
  const navigate = useNavigate();
  
  // 【修改点 1】新增 codeForm 实例
  const [codeForm] = Form.useForm(); 

  // ========== 密码登录 ==========
  const onPasswordLogin = async (values: { username: string; password: string }) => {
    setLoading(true);
    try {
      const response = await api.post('/token', new URLSearchParams({
        username: values.username,
        password: values.password,
      }), {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      });
      localStorage.setItem('zhishentong_token', response.data.access_token);
      message.success('登录成功');
      window.location.href = '/dashboard';
    } catch (error: any) {
      message.error(error.response?.data?.detail || '用户名或密码错误');
    } finally {
      setLoading(false);
    }
  };

  // ========== 发送验证码 ==========
  const handleSendCode = async (target: string) => {
    if (!target) {
      message.warning('请输入手机号或邮箱');
      return;
    }
    const isPhone = /^1\d{10}$/.test(target);
    const isEmail = /@/.test(target);
    if (!isPhone && !isEmail) {
      message.warning('请输入正确的手机号或邮箱');
      return;
    }
    const type = isPhone ? 'phone' : 'email';
    setCodeLoading(true);
    try {
      await api.post('/send-code', { target, type });
      message.success('验证码已发送');
      setCountdown(60);
      const timer = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) { clearInterval(timer); return 0; }
          return prev - 1;
        });
      }, 1000);
    } catch (error: any) {
      message.error(error.response?.data?.detail || '发送失败');
    } finally {
      setCodeLoading(false);
    }
  };

  // ========== 验证码登录 ==========
  const onCodeLogin = async (values: { target: string; code: string }) => {
    setLoading(true);
    try {
      const response = await api.post('/login-code', {
        target: values.target,
        code: values.code,
        code_type: /^1\d{10}$/.test(values.target) ? 'phone' : 'email',
      });
      localStorage.setItem('zhishentong_token', response.data.access_token);
      message.success('登录成功');
      window.location.href = '/dashboard';
    } catch (error: any) {
      message.error(error.response?.data?.detail || '验证码错误或已过期');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.loginContainer}>
      {/* ===== 左侧品牌区 ===== */}
      <div className={styles.brandSection}>
        <div className={styles.logo}>
          <div className={styles.logoIcon}>⚖</div>
          <div className={styles.logoText}>智审<span>通</span></div>
        </div>
        <h1 className={styles.brandTitle}>
          审合同，就找<span>“智审通”</span>
        </h1>
        <p className={styles.brandSubtitle}>
          AI 驱动的全生命周期合同审核与管理平台，<br />
          帮助企业精准识别风险，提升审核效率。
        </p>
        <div className={styles.features}>
          <div className={styles.featureItem}>
            <CheckCircleOutlined className={styles.featureIcon} />
            风险识别率 98%+
          </div>
          <div className={styles.featureItem}>
            <SafetyOutlined className={styles.featureIcon} />
            数据安全加密
          </div>
          <div className={styles.featureItem}>
            <FileTextOutlined className={styles.featureIcon} />
            并排比对 + 一键采纳
          </div>
          <div className={styles.featureItem}>
            <TeamOutlined className={styles.featureIcon} />
            全生命周期管理
          </div>
        </div>
      </div>

      {/* ===== 右侧登录区 ===== */}
      <div className={styles.loginSection}>
        <div className={styles.loginCard}>
          <h1>欢迎登录</h1>
          <p className={styles.subtitle}>登录您的智审通账号，开始智能合同审核</p>

          <Tabs
            activeKey={loginType}
            onChange={(key) => setLoginType(key as 'password' | 'code')}
            className={styles.tabs}
          >
            <TabPane tab="密码登录" key="password">
              <Form
                name="passwordLogin"
                onFinish={onPasswordLogin}
                autoComplete="off"
                size="large"
                layout="vertical"
              >
                <Form.Item
                  name="username"
                  rules={[{ required: true, message: '请输入用户名/手机号/邮箱' }]}
                  className={styles.formItem}
                >
                  <Input
                    prefix={<UserOutlined />}
                    placeholder="用户名 / 手机号 / 邮箱"
                  />
                </Form.Item>

                <Form.Item
                  name="password"
                  rules={[{ required: true, message: '请输入密码' }]}
                  className={styles.formItem}
                >
                  <Input.Password
                    prefix={<LockOutlined />}
                    placeholder="密码"
                  />
                </Form.Item>

                <Form.Item>
                  <Button
                    type="primary"
                    htmlType="submit"
                    block
                    loading={loading}
                    className={styles.loginButton}
                  >
                    登录
                  </Button>
                </Form.Item>
              </Form>
            </TabPane>

            <TabPane tab="验证码登录" key="code">
              {/* 【修改点 2】把 form={codeForm} 绑定到验证码表单 */}
              <Form
                form={codeForm}
                name="codeLogin"
                onFinish={onCodeLogin}
                autoComplete="off"
                size="large"
                layout="vertical"
              >
                <Form.Item
                  name="target"
                  rules={[{ required: true, message: '请输入手机号或邮箱' }]}
                  className={styles.formItem}
                >
                  <Input
                    prefix={<PhoneOutlined />}
                    placeholder="手机号 / 邮箱"
                  />
                </Form.Item>

                <Form.Item
                  name="code"
                  rules={[{ required: true, message: '请输入验证码' }]}
                  className={styles.formItem}
                >
                  <Input
                    placeholder="验证码"
                    suffix={
                      <Button
                        type="link"
                        onClick={() => {
                          // 【修改点 3】使用 codeForm.getFieldValue 获取手机号
                          const target = codeForm.getFieldValue('target');
                          if (target) handleSendCode(target);
                          else message.warning('请先输入手机号或邮箱');
                        }}
                        disabled={countdown > 0 || codeLoading}
                        style={{ padding: 0 }}
                      >
                        {countdown > 0 ? `${countdown}s` : '获取验证码'}
                      </Button>
                    }
                  />
                </Form.Item>

                <Form.Item>
                  <Button
                    type="primary"
                    htmlType="submit"
                    block
                    loading={loading}
                    className={styles.loginButton}
                  >
                    登录
                  </Button>
                </Form.Item>
              </Form>
            </TabPane>
          </Tabs>

          <div className={styles.registerLink}>
            还没有账号？ <Link to="/register">立即注册</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;